import React, { Component } from 'react'
import { MDBRow, MDBCol, MDBSpinner } from 'mdb-react-ui-kit'
import { Subscription } from 'rxjs'
import VimeoPlayer from '@vimeo/player'
import TrainingsPageService from '../../shared/TrainingsPage.service'
import TrainingQuiz from '../TrainingQuiz/TrainingQuiz.component'
import TrainingQuizService from '../../../../shared/services/TrainingQuiz.service'
import TrainingQuestionService from '../../../../shared/services/TrainingQuestion.service'
import TrainingAnswerService from '../../../../shared/services/TrainingAnswer.service'

// Largest gap between consecutive timeupdate positions still treated as continuous playback.
// Ticks arrive every ~250ms (~0.5s of media at 2x); anything larger is a seek.
const MAX_PLAYED_STEP_SECONDS = 2
// Credit per tick is capped at wall-clock time × this rate (Vimeo's fastest speed, 2x, plus 25% for
// uneven tick timing), so scrubbing the playhead while playing can't count faster than watching.
const MAX_CREDIT_RATE = 2.5
// While the quiz is locked, a seek further than this past the furthest watched point is sent back.
const SKIP_AHEAD_TOLERANCE_SECONDS = 1
// The first moment after play is never reported, so 100% would be unreachable without this slack.
const WATCHED_SLACK_SECONDS = 1
// ready() neither resolves nor rejects when the player iframe never loads (e.g. Vimeo is blocked).
const PLAYER_READY_TIMEOUT_MS = 20000
const TRACKED_PLAYER_EVENTS = ['timeupdate', 'play', 'pause', 'ended', 'seeking', 'seeked', 'error']

// Video + quiz for a training that requires a quiz. The quiz stays read-only until the agent has
// played the unlock threshold of the video, unless they already passed it. Mount one per opened
// training (key by training id) so watch progress starts from zero each time.
class TrainingVideoQuiz extends Component {
  state = {
    video_watch: { status: 'loading', played: [], duration: 0 },
    quiz_unlock_percent: TrainingsPageService.QUIZ_UNLOCK_WATCH_PERCENT,
    user_training_quizes: [],
    training_quiz: null,
    training_questions: [],
    training_answers: [],
    training_quiz_loading: true,
  }

  #subscriptions$ = new Subscription()
  #player = null
  #lastPosition = null
  #lastTickAt = null
  #playing = false
  #readyTimer = null
  #unmounted = false

  #getLink = () => `${this.props.training?.training_link || ''}`.replace(/(<([^>]+)>)/gi, '')

  componentDidMount() {
    if (!TrainingsPageService.isVimeoPlayerLink(this.#getLink()))
      this.setState({ video_watch: { status: 'failed', played: [], duration: 0 } })

    this.#subscriptions$.add(
      TrainingsPageService.getUserTrainingQuizes().subscribe(user_training_quizes =>
        this.setState({ user_training_quizes: Array.isArray(user_training_quizes) ? user_training_quizes : [] })
      )
    )
    TrainingsPageService.fetchQuizUnlockWatchPercent().then(
      quiz_unlock_percent => !this.#unmounted && this.setState({ quiz_unlock_percent })
    )
    this.#fetchQuiz()
  }

  componentWillUnmount() {
    this.#unmounted = true
    this.#subscriptions$.unsubscribe()
    this.#untrackVideo()
  }

  #fetchQuiz = async () => {
    const training_quiz = (
      await TrainingQuizService.search({
        search: { training_id: this.props.training.id },
      })
    )?.models[0]
    const training_questions =
      (
        await TrainingQuestionService.search({
          search: { quiz_id: training_quiz?.id },
        })
      )?.models ?? []
    const question_ids = training_questions.map(q => q.id)
    const training_answers =
      (
        await TrainingAnswerService.search({
          search: {
            question_id: question_ids?.length > 0 ? question_ids : null,
          },
        })
      )?.models ?? []

    if (!this.#unmounted)
      this.setState({ training_quiz, training_questions, training_answers, training_quiz_loading: false })
  }

  #setWatch = (player, watch) => {
    if (this.#player === player) this.setState(prev => ({ video_watch: { ...prev.video_watch, ...watch } }))
  }

  // Built from consecutive positions while playing rather than player.getPlayed(): Vimeo reports
  // [0, x] as played when the first play starts after a seek to x, and timeupdate also fires while
  // the playhead is dragged on a paused video.
  #recordPlayed = (player, seconds, duration) => {
    if (this.#player !== player) return
    const from = this.#playing ? this.#lastPosition : null,
      now = Date.now(),
      elapsed = this.#lastTickAt === null ? 0 : (now - this.#lastTickAt) / 1000
    this.#lastPosition = seconds
    this.#lastTickAt = now
    const continuous = from !== null && seconds > from && seconds - from <= MAX_PLAYED_STEP_SECONDS,
      to = continuous ? Math.min(seconds, from + elapsed * MAX_CREDIT_RATE) : from

    this.setState(prev => {
      const played = [...prev.video_watch.played]
      if (continuous && to > from) {
        const last = played[played.length - 1]
        if (last && last[1] === from) played[played.length - 1] = [last[0], to]
        else played.push([from, to])
      }
      return { video_watch: { ...prev.video_watch, played, duration } }
    })
  }

  #breakContinuity = () => {
    this.#lastPosition = null
    this.#lastTickAt = null
  }

  #holdAtFurthestWatched = (player, seconds) => {
    if (this.#player !== player || !this.#getQuizLockedMessage()) return
    const furthest = Math.max(0, ...this.state.video_watch.played.map(range => range[1]))
    if (seconds > furthest + SKIP_AHEAD_TOLERANCE_SECONDS) player.setCurrentTime(furthest).catch(() => {})
  }

  #hasWatchedEnough = ({ played, duration }) =>
    duration > 0 &&
    TrainingsPageService.getPlayedSeconds(played) + WATCHED_SLACK_SECONDS >=
      (duration * this.state.quiz_unlock_percent) / 100

  #trackVideo = iframe => {
    this.#untrackVideo()
    let player
    try {
      player = new VimeoPlayer(iframe)
    } catch (ex) {
      this.setState({ video_watch: { status: 'failed', played: [], duration: 0 } })
      return
    }
    this.#player = player
    this.#playing = false
    this.#breakContinuity()

    this.#readyTimer = setTimeout(() => {
      if (this.state.video_watch.status === 'loading') this.#setWatch(player, { status: 'failed' })
    }, PLAYER_READY_TIMEOUT_MS)
    player
      .ready()
      .then(() => this.#setWatch(player, { status: 'tracking' }))
      .catch(() => this.#setWatch(player, { status: 'failed' }))

    player.on('timeupdate', ({ seconds, duration }) => this.#recordPlayed(player, seconds, duration))
    player.on('play', () => {
      this.#playing = true
      this.#breakContinuity()
    })
    player.on('pause', () => (this.#playing = false))
    player.on('ended', () => (this.#playing = false))
    player.on('seeking', () => this.#breakContinuity())
    player.on('seeked', ({ seconds }) => {
      this.#breakContinuity()
      this.#holdAtFurthestWatched(player, seconds)
    })

    player.on('error', () => {
      if (!this.#hasWatchedEnough(this.state.video_watch)) this.#setWatch(player, { status: 'failed' })
    })
  }

  #untrackVideo = () => {
    clearTimeout(this.#readyTimer)
    if (!this.#player) return
    TRACKED_PLAYER_EVENTS.forEach(event => this.#player.off(event))
    this.#player = null
    this.#playing = false
  }

  #onVideoFrame = iframe => (iframe ? this.#trackVideo(iframe) : this.#untrackVideo())

  #isQuizPassed = () =>
    this.state.user_training_quizes.some(
      quiz => parseInt(quiz.training_id) === parseInt(this.props.training?.id) && parseInt(quiz.passed) === 1
    )

  #getQuizLockedMessage = () => {
    if (this.#isQuizPassed()) return null
    if (this.state.video_watch.status === 'failed') return "We can't verify you watched this video. Contact support."
    if (this.#hasWatchedEnough(this.state.video_watch)) return null
    return 'Watch the video, then take your quiz.'
  }

  render() {
    const link = this.#getLink()

    return (
      <MDBRow>
        <MDBCol size='12' lg='8'>
          {link ? (
            <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
              <iframe
                ref={TrainingsPageService.isVimeoPlayerLink(link) ? this.#onVideoFrame : undefined}
                src={link}
                frameBorder='0'
                allow='autoplay; fullscreen; picture-in-picture'
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
                title={this.props.training?.training_name}
              ></iframe>
            </div>
          ) : (
            <p className='text-muted mb-0'>No video link available.</p>
          )}
        </MDBCol>
        <MDBCol size='12' lg='4' className='mt-4 mt-lg-0'>
          {this.state.training_quiz_loading ? (
            <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
              <MDBSpinner></MDBSpinner>
            </div>
          ) : (
            <TrainingQuiz
              training_quiz={this.state.training_quiz}
              training_questions={this.state.training_questions}
              training_answers={this.state.training_answers}
              lockedMessage={this.#getQuizLockedMessage()}
              onSuccess={this.props.onSuccess}
            />
          )}
        </MDBCol>
      </MDBRow>
    )
  }
}

export default TrainingVideoQuiz
